import React, { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Upload, Trash2, Edit3, Search, X, Phone, Mail, Calendar, NotebookPen, ArrowUpDown, GraduationCap, ClipboardPaste, MailCheck, Send, Archive, ArchiveRestore } from "lucide-react";
import api from "../../lib/api";
import BulkPasteModal from "../../components/BulkPasteModal";
import ColumnOrderMenu from "../../components/ColumnOrderMenu";
import useColumnPrefs from "../../lib/useColumnPrefs";
import { safeHtml } from "../../lib/safeHtml";

// Column registry for the Leads desktop table. Reorder/hide preferences
// persist per-admin via /api/admin/me/prefs. "select" and "actions" columns
// stay pinned to the ends (locked).
const LEAD_COLUMNS_META = [
  { id: "select", label: "Select checkbox", locked: true },
  { id: "enquiry", label: "Enquiry date" },
  { id: "name", label: "Name" },
  { id: "contact", label: "Contact" },
  { id: "session", label: "Session preference" },
  { id: "source", label: "Source" },
  { id: "status", label: "Status" },
  { id: "actions", label: "Actions", locked: true },
];

// Fallback statuses used before the /admin/settings/public payload lands —
// once the API responds these are replaced by the admin-editable list from
// Settings → Lead statuses.
const FALLBACK_STATUSES = ["new", "warm", "hot", "kit", "dropped_out", "student", "returner", "past_student", "closed"];
const FALLBACK_LABELS = { new: "New", warm: "Warm", hot: "Hot", kit: "KIT", dropped_out: "Dropped out", student: "Enrolled", returner: "Returner", past_student: "Past student", closed: "Closed" };
const FALLBACK_COLORS = { new: "#8EC9C7", warm: "#C9A24E", hot: "#A685BD", kit: "#6E6074", dropped_out: "#D77A9D", student: "#5D2C8F", returner: "#5D2C8F", past_student: "#9A8AA6", closed: "#9A8AA6" };
const SESSION_OPTS = [
  { v: "", lbl: "— No preference —" },
  { v: "tue_am", lbl: "Tuesday AM (10–1)" },
  { v: "tue_pm", lbl: "Tuesday PM (2–5)" },
  { v: "fri_eve", lbl: "Friday Evening (6–8:30)" },
];
const sessionLabel = (v) => SESSION_OPTS.find(o => o.v === v)?.lbl || v || "—";

// Compact enquiry date: "27 Mar" (this year) or "27 Mar 25" (older).
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function compactDate(raw, fallback) {
  const src = raw || fallback;
  if (!src) return "—";
  // Try ISO / Date-parseable first.
  let d = new Date(src);
  if (!isNaN(d.getTime())) {
    const thisYear = new Date().getFullYear();
    const dayPart = `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
    return d.getFullYear() === thisYear ? dayPart : `${dayPart} ${String(d.getFullYear()).slice(-2)}`;
  }
  // Try DD.MM.YY / DD/MM/YYYY style strings (legacy spreadsheet imports).
  const m = String(src).match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const monIdx = parseInt(m[2], 10) - 1;
    let yr = parseInt(m[3], 10);
    if (yr < 100) yr += 2000;
    if (monIdx >= 0 && monIdx < 12) {
      const dayPart = `${day} ${MONTHS_SHORT[monIdx]}`;
      const thisYear = new Date().getFullYear();
      return yr === thisYear ? dayPart : `${dayPart} ${String(yr).slice(-2)}`;
    }
  }
  return String(src).slice(0, 10);
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sessionFilter, setSessionFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [converting, setConverting] = useState(null); // lead being converted to student
  const [settings, setSettings] = useState(null);
  const [pasting, setPasting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [acking, setAcking] = useState(null); // lead currently being acknowledged
  const [colPrefs, setColPrefs] = useColumnPrefs("leads", { order: [], hidden: [] });
  // "active" (default — hides converted leads) or "archived" (shows only
  // converted / manually-archived ones). Auto-archive fires backend-side
  // whenever a lead's status flips to `student`, so the active list stays
  // clean without admin intervention.
  const [tab, setTab] = useState("active");
  const [counts, setCounts] = useState({ active: 0, archived: 0 });

  const load = async () => {
    const params = tab === "archived" ? { archived_only: true } : {};
    const [{ data }, countsResp] = await Promise.all([
      api.get("/admin/leads", { params }),
      api.get("/admin/leads/counts").catch(() => null),
    ]);
    setLeads(data);
    if (countsResp?.data) setCounts(countsResp.data);
    // refresh detail panel with latest data
    setDetail((cur) => (cur ? data.find((l) => l.id === cur.id) || null : null));
  };

  // Dynamic status list, seeded from settings.lead_statuses. Rendered
  // everywhere the old hard-coded STATUSES / STATUS_LABEL / STATUS_COLOR
  // constants used to be — dropdown, filter chips, edit modal, drawer.
  // Memoized so Object.fromEntries isn't rebuilt on every render (only
  // when the underlying settings.lead_statuses array actually changes).
  const { STATUSES, STATUS_LABEL, STATUS_COLOR } = useMemo(() => {
    const statusList = (settings?.lead_statuses && settings.lead_statuses.length)
      ? settings.lead_statuses
      : FALLBACK_STATUSES.map((k) => ({ key: k, label: FALLBACK_LABELS[k] || k, color: FALLBACK_COLORS[k] || "#5D2C8F" }));
    return {
      STATUSES: statusList.map((s) => s.key),
      STATUS_LABEL: Object.fromEntries(statusList.map((s) => [s.key, s.label])),
      STATUS_COLOR: Object.fromEntries(statusList.map((s) => [s.key, s.color || "#5D2C8F"])),
    };
  }, [settings?.lead_statuses]);
  // Initial load runs once on mount; subsequent refreshes are triggered
  // explicitly by save/delete/etc. handlers. Wrapped with a cancelled flag
  // so a fast unmount can't race a network response into setState.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // /admin/settings 403s for staff without the settings perm — fall
        // back to the public slice so bulk-paste/session pickers still
        // render correctly for them.
        const params = tab === "archived" ? { archived_only: true } : {};
        const [{ data: leadsData }, countsResp, settingsResp, publicSettingsResp] = await Promise.all([
          api.get("/admin/leads", { params }),
          api.get("/admin/leads/counts").catch(() => null),
          api.get("/admin/settings").catch(() => null),
          api.get("/admin/settings/public").catch(() => null),
        ]);
        if (cancelled) return;
        setLeads(leadsData);
        if (countsResp?.data) setCounts(countsResp.data);
        const s = settingsResp?.data || publicSettingsResp?.data;
        if (s) setSettings(s);
      } catch (e) { /* surface via empty state */ }
    })();
    return () => { cancelled = true; };
  }, [tab]);

  const filtered = leads.filter((l) => {
    if (statusFilter && l.status !== statusFilter) return false;
    if (sourceFilter && (l.source || "") !== sourceFilter) return false;
    if (sessionFilter && (l.session_preference || "") !== sessionFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.name || "").toLowerCase().includes(s)
      || (l.email || "").toLowerCase().includes(s)
      || (l.phone || "").includes(s)
      || (l.notes || "").toLowerCase().includes(s);
  });

  // Sort the filtered list — column-header sortable
  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const get = (l) => {
      switch (sortBy) {
        case "name": return (l.name || "").toLowerCase();
        case "email": return (l.email || "").toLowerCase();
        case "source": return (l.source || "").toLowerCase();
        case "session_preference": return l.session_preference || "";
        case "status": return l.status || "";
        case "enquiry_date": return l.enquiry_sort_date || l.enquiry_date || l.created_at || "";
        default: return l.created_at || "";
      }
    };
    return [...filtered].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filtered, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "name" || key === "source" ? "asc" : "desc"); }
  };

  // Dropdown options derived from the live lead list — no hard-coded lists.
  const options = {
    sources: Array.from(new Set(leads.map((l) => l.source).filter(Boolean))).sort(),
    sessions: Array.from(new Set(leads.map((l) => l.session_preference).filter(Boolean))).sort(),
  };

  const importSeed = async () => {
    setBusy(true);
    try {
      const [{ data: l }, { data: s }] = await Promise.all([
        api.post("/admin/leads/import-seed"),
        api.post("/admin/students/import-seed"),
      ]);
      toast.success(`Imported ${l.imported} leads and ${s.imported} historic students`);
      await load();
    } catch { toast.error("Import failed"); } finally { setBusy(false); }
  };

  const submitBulkPaste = async (body) => {
    const { data } = await api.post("/admin/leads/bulk-paste", body);
    await load();
    return data;
  };

  const toggleSelect = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllVisible = (visible, allChecked) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (allChecked) visible.forEach((l) => next.delete(l.id));
      else visible.forEach((l) => next.add(l.id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!confirm(`Permanently delete ${ids.length} lead${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/leads/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} lead${data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const bulkArchive = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!confirm(`Archive ${ids.length} lead${ids.length === 1 ? "" : "s"}? They'll move to the Archived tab and can be restored any time.`)) return;
    try {
      const { data } = await api.post("/admin/leads/bulk-archive", { ids, reason: "bulk" });
      toast.success(`Archived ${data.archived} lead${data.archived === 1 ? "" : "s"}`);
      clearSelection();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
  };
  const bulkUnarchive = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/admin/leads/bulk-unarchive", { ids });
      toast.success(`Restored ${data.restored} lead${data.restored === 1 ? "" : "s"}`);
      clearSelection();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const saveLead = async (form) => {
    try {
      // Special case: an existing lead's status has just been flipped to
      // "student" from the edit form. Route through the Enrol / auto-invoice
      // modal same as the inline status pill on the list — otherwise the
      // student ends up marked "Enrolled" on the CRM board but never lands
      // on the term roster and never gets an invoice.
      if (editing?.id && form.status === "student" && editing.status !== "student") {
        const { status: _newStatus, ...restFields } = form;
        // Save every OTHER field the admin edited (name, email, notes, etc.)
        // before opening the enrol modal, so their edits aren't lost if
        // they cancel the enrolment step.
        if (Object.keys(restFields).length) {
          await api.patch(`/admin/leads/${editing.id}`, restFields);
        }
        const refreshed = { ...editing, ...restFields };
        setEditing(null);
        setShowForm(false);
        setConverting(refreshed);
        return;
      }
      if (editing?.id) {
        await api.patch(`/admin/leads/${editing.id}`, form);
        toast.success("Lead updated");
      } else {
        await api.post("/admin/leads", form);
        toast.success("Lead added");
      }
      setEditing(null); setShowForm(false);
      await load();
    } catch { toast.error("Save failed"); }
  };

  const setStatus = async (lead, status) => {
    // Special-case: converting to student opens a modal so the admin can
    // confirm the term + session and auto-create an invoice.
    if (status === "student" && lead.status !== "student") {
      setConverting(lead);
      return;
    }
    await api.patch(`/admin/leads/${lead.id}`, { status });
    await load();
  };

  const submitConversion = async (form) => {
    if (!converting) return;
    try {
      const { data } = await api.post(`/admin/leads/${converting.id}/convert-to-student`, form);
      const inv = data.invoice;
      toast.success(inv ? `Enrolled · invoice ${inv.invoice_number} created` : "Enrolled — no invoice created");
      setConverting(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); }
  };

  const setLeadSession = async (lead, session_preference) => {
    try { await api.patch(`/admin/leads/${lead.id}`, { session_preference }); toast.success("Session updated"); await load(); }
    catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Deleted");
    if (detail?.id === id) setDetail(null);
    await load();
  };

  const archive = async (id) => {
    if (!confirm("Archive this lead? It'll be hidden from the active list but you can restore it any time from the Archived tab.")) return;
    try {
      await api.post(`/admin/leads/${id}/archive`, { reason: "manual" });
      toast.success("Lead archived");
      if (detail?.id === id) setDetail(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  };

  const unarchive = async (id) => {
    try {
      await api.post(`/admin/leads/${id}/unarchive`);
      toast.success("Lead restored");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  return (
    <div data-testid="admin-leads-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">CRM</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Leads</h1>
          <p className="text-sm text-[#6E6074] mt-1">Hover a row for full notes, or click a name to open the contact.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPasting(true)} className="sac-btn-secondary" data-testid="leads-paste-btn"><ClipboardPaste size={16} /> Paste from spreadsheet</button>
          <button onClick={importSeed} disabled={busy} className="sac-btn-secondary" data-testid="leads-import-btn"><Upload size={16} /> Import seed</button>
          <button onClick={() => { setEditing({}); setShowForm(true); }} className="sac-btn-primary" data-testid="leads-add-btn"><Plus size={16} /> Add lead</button>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b border-[#E5E0D8]" data-testid="leads-tabs">
        {[
          { key: "active", label: "Active", count: counts.active, testid: "leads-tab-active" },
          { key: "archived", label: "Archived", count: counts.archived, testid: "leads-tab-archived" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setSelected(new Set()); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#5D2C8F] text-[#5D2C8F]" : "border-transparent text-[#6E6074] hover:text-[#5D2C8F]"}`}
            data-testid={t.testid}
          >
            {t.label}
            <span className="ml-2 text-xs bg-[#F1EAE0] text-[#5D2C8F] px-1.5 py-0.5 rounded-full">{t.count}</span>
          </button>
        ))}
        {tab === "archived" && (
          <span className="ml-auto text-xs text-[#6E6074] pb-2 pr-2">Converted leads land here automatically. Restore any of them below.</span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-[#6E6074]" />
          <input data-testid="leads-search" placeholder="Search name, email, phone, notes"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 bg-white border border-[#E5E0D8] rounded-lg text-sm w-80 focus:outline-none focus:ring-2 focus:ring-[#5D2C8F]" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="leads-status-filter"
          className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
        </select>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
          data-testid="leads-source-filter"
          className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm">
          <option value="">All sources</option>
          {options.sources.map((s) => <option key={s} value={s}>{s.length > 40 ? s.slice(0, 40) + '…' : s}</option>)}
        </select>
        <select value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)}
          data-testid="leads-session-filter"
          className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm">
          <option value="">All sessions</option>
          {options.sessions.map((s) => <option key={s} value={s}>{s.length > 40 ? s.slice(0, 40) + '…' : s}</option>)}
        </select>
        {(search || statusFilter || sourceFilter || sessionFilter) && (
          <button onClick={() => { setSearch(""); setStatusFilter(""); setSourceFilter(""); setSessionFilter(""); }} className="text-xs text-[#A685BD] hover:underline" data-testid="leads-clear-filters">Clear filters</button>
        )}
        <ColumnOrderMenu
          columns={LEAD_COLUMNS_META}
          value={colPrefs}
          onChange={setColPrefs}
          testid="leads-columns-menu"
        />
        <span className="ml-auto text-sm text-[#6E6074]">{sorted.length} lead{sorted.length === 1 ? "" : "s"}</span>
      </div>

      <div className="admin-surface">
        {selected.size > 0 && (
          <div className="flex items-center justify-between gap-3 p-3 bg-[#F4EDDD] border-b border-[#E5E0D8]" data-testid="leads-bulk-bar">
            <div className="text-sm text-[#5D2C8F]"><strong>{selected.size}</strong> lead{selected.size === 1 ? "" : "s"} selected</div>
            <div className="flex items-center gap-3">
              <button onClick={clearSelection} className="text-xs text-[#6E6074] hover:underline" data-testid="leads-bulk-clear">Clear selection</button>
              {tab === "archived" ? (
                <button
                  onClick={bulkUnarchive}
                  className="bg-[#5D2C8F] hover:bg-[#4a2170] text-white rounded-full px-4 py-1.5 text-xs inline-flex items-center gap-1.5 transition-colors"
                  data-testid="leads-bulk-unarchive"
                >
                  <ArchiveRestore size={12} /> Restore {selected.size} selected
                </button>
              ) : (
                <button
                  onClick={bulkArchive}
                  className="bg-[#8EC9C7] hover:bg-[#71b6b3] text-[#1E5F5A] rounded-full px-4 py-1.5 text-xs inline-flex items-center gap-1.5 transition-colors font-medium"
                  data-testid="leads-bulk-archive"
                >
                  <Archive size={12} /> Archive {selected.size} selected
                </button>
              )}
              <button onClick={bulkDelete} className="bg-[#A685BD] hover:bg-[#8e6da9] text-white rounded-full px-4 py-1.5 text-xs inline-flex items-center gap-1.5 transition-colors" data-testid="leads-bulk-delete">
                <Trash2 size={12} /> Delete {selected.size} selected
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <LeadsTableBody
            rows={sorted}
            colPrefs={colPrefs}
            sortBy={sortBy}
            sortDir={sortDir}
            toggleSort={toggleSort}
            selected={selected}
            toggleSelect={toggleSelect}
            toggleSelectAllVisible={toggleSelectAllVisible}
            handlers={{ setDetail, setLeadSession, setStatus, setEditing, setShowForm, remove, setAcking, archive, unarchive, tab }}
            STATUSES={STATUSES}
            STATUS_LABEL={STATUS_LABEL}
            STATUS_COLOR={STATUS_COLOR}
          />
        </table>
        </div>
      </div>

      {showForm && <LeadForm lead={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSave={saveLead} STATUSES={STATUSES} STATUS_LABEL={STATUS_LABEL} />}

      {detail && <LeadDrawer lead={detail} onClose={() => setDetail(null)} onEdit={() => { setEditing(detail); setShowForm(true); setDetail(null); }} onStatus={(s) => setStatus(detail, s)} onDelete={() => remove(detail.id)} onSendAck={() => { setAcking(detail); setDetail(null); }} STATUSES={STATUSES} STATUS_LABEL={STATUS_LABEL} STATUS_COLOR={STATUS_COLOR} />}

      {converting && <ConvertLeadModal lead={converting} settings={settings} onClose={() => setConverting(null)} onSubmit={submitConversion} />}
      {pasting && <BulkPasteModal kind="leads" settings={settings} onClose={() => setPasting(false)} onSubmit={submitBulkPaste} />}
      {acking && <SendAckModal lead={acking} onClose={() => setAcking(null)} onSent={async (updatedLead) => { setAcking(null); await load(); toast.success(`Thank-you email sent to ${updatedLead?.email || acking.email} ✓`); }} />}
    </div>
  );
}

function LeadSortBtn({ label, k, sortBy, sortDir, onClick }) {
  const active = sortBy === k;
  return (
    <button onClick={() => onClick(k)} className={`inline-flex items-center gap-1 hover:text-[#A685BD] transition-colors ${active ? "underline decoration-[#A685BD] underline-offset-4" : ""}`} data-testid={`leads-sort-${k}`}>
      {label} <ArrowUpDown size={11} className={active ? "opacity-100" : "opacity-30"} />
      {active && <span className="text-[10px] ml-0.5">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );
}

/**
 * Renders the Leads table thead + tbody respecting the admin's saved
 * column-order / hidden prefs.  "select" stays pinned first, "actions" last.
 */
function LeadsTableBody({ rows, colPrefs, sortBy, sortDir, toggleSort, selected, toggleSelect, toggleSelectAllVisible, handlers, STATUSES, STATUS_LABEL, STATUS_COLOR }) {
  const { setDetail, setLeadSession, setStatus, setEditing, setShowForm, remove, setAcking, archive, unarchive, tab } = handlers;
  const hidden = new Set(colPrefs?.hidden || []);
  const raw = (colPrefs?.order && colPrefs.order.length)
    ? colPrefs.order.filter((id) => LEAD_COLUMNS_META.some((c) => c.id === id))
    : LEAD_COLUMNS_META.map((c) => c.id);
  for (const c of LEAD_COLUMNS_META) if (!raw.includes(c.id)) raw.push(c.id);
  const ordered = [
    "select",
    ...raw.filter((id) => id !== "select" && id !== "actions"),
    "actions",
  ];
  const visible = ordered.filter((id) => !hidden.has(id));

  const allChecked = rows.length > 0 && rows.every((l) => selected.has(l.id));

  const th = (id) => {
    switch (id) {
      case "select":
        return (
          <input type="checkbox"
            checked={allChecked}
            ref={(el) => { if (el) el.indeterminate = selected.size > 0 && !allChecked; }}
            onChange={() => toggleSelectAllVisible(rows, allChecked)}
            aria-label="Select all visible"
            data-testid="leads-select-all"
          />
        );
      case "enquiry": return <LeadSortBtn label="Enquiry" k="enquiry_date" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "name": return <LeadSortBtn label="Name" k="name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "contact": return <LeadSortBtn label="Contact" k="email" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "session": return <LeadSortBtn label="Session pref." k="session_preference" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "source": return <LeadSortBtn label="Source" k="source" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "status": return <LeadSortBtn label="Status" k="status" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "actions": return "";
      default: return id;
    }
  };

  const widthFor = (id) => ({ select: "w-10", enquiry: "w-20", status: "w-28", actions: "w-20" }[id] || "");

  const td = (id, l) => {
    switch (id) {
      case "select":
        return (
          <td className="px-3 py-3 align-top">
            <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)}
              aria-label={`Select ${l.name}`} data-testid={`lead-select-${l.id}`} />
          </td>
        );
      case "enquiry":
        return <td className="px-2 py-3 text-[#6E6074] text-xs align-top whitespace-nowrap">{compactDate(l.enquiry_date, l.created_at)}</td>;
      case "name":
        return (
          <td className="px-3 py-3 align-top">
            <button onClick={() => setDetail(l)} className="text-left" data-testid={`lead-open-${l.id}`}>
              <div className="font-medium text-[#5D2C8F] hover:underline decoration-[#8EC9C7] decoration-2 underline-offset-4">{l.name}</div>
            </button>
            {l.notes && <HoverNotes notes={l.notes} />}
          </td>
        );
      case "contact":
        return (
          <td className="px-3 py-3 text-[#5D2C8F] align-top">
            <div>{l.email}</div>
            <div className="text-[#6E6074]">{l.phone}</div>
          </td>
        );
      case "session":
        return (
          <td className="px-3 py-3 text-[#6E6074] align-top">
            <select value={SESSION_OPTS.some(o => o.v === l.session_preference) ? l.session_preference : ""}
              onChange={(e) => setLeadSession(l, e.target.value)}
              className="bg-white border border-[#E5E0D8] rounded px-2 py-1 text-xs"
              data-testid={`lead-session-${l.id}`}>
              {SESSION_OPTS.map(o => <option key={o.v} value={o.v}>{o.lbl}</option>)}
            </select>
            {l.session_preference && !SESSION_OPTS.some(o => o.v === l.session_preference) && (
              <div className="text-[10px] text-[#A685BD] mt-1">was: {l.session_preference}</div>
            )}
          </td>
        );
      case "source":
        return <td className="px-3 py-3 text-[#6E6074] align-top max-w-[160px] truncate" title={l.source}>{l.source}</td>;
      case "status":
        return (
          <td className="px-3 py-3 align-top">
            <select value={l.status} onChange={(e) => setStatus(l, e.target.value)}
              className="text-xs bg-transparent border border-[#E5E0D8] rounded-full px-2 py-1"
              style={{ color: STATUS_COLOR[l.status] || "#5D2C8F" }}
              data-testid={`lead-status-${l.id}`}>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
            </select>
          </td>
        );
      case "actions":
        return (
          <td className="px-3 py-3 text-right align-top whitespace-nowrap">
            <button
              onClick={() => setAcking(l)}
              disabled={!l.email}
              title={l.email ? (l.enquiry_ack_sent_at ? `Re-send enquiry thank-you (last sent ${new Date(l.enquiry_ack_sent_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})` : "Send enquiry thank-you email") : "No email on file"}
              className={`p-1.5 hover:bg-white rounded ${l.enquiry_ack_sent_at ? "text-[#5D2C8F]" : "text-[#8EC9C7]"} disabled:opacity-30 disabled:cursor-not-allowed`}
              data-testid={`lead-send-ack-${l.id}`}
            >
              {l.enquiry_ack_sent_at ? <MailCheck size={14} /> : <Mail size={14} />}
            </button>
            <button onClick={() => { setEditing(l); setShowForm(true); }} className="p-1.5 hover:bg-white rounded" data-testid={`lead-edit-${l.id}`}><Edit3 size={14} /></button>
            {tab === "archived" ? (
              <button onClick={() => unarchive(l.id)} className="p-1.5 hover:bg-white rounded text-[#5D2C8F]" title="Restore to active leads" data-testid={`lead-unarchive-${l.id}`}><ArchiveRestore size={14} /></button>
            ) : (
              <button onClick={() => archive(l.id)} className="p-1.5 hover:bg-white rounded text-[#A685BD]" title="Archive lead (hide from active table)" data-testid={`lead-archive-${l.id}`}><Archive size={14} /></button>
            )}
            <button onClick={() => remove(l.id)} className="p-1.5 hover:bg-white rounded text-[#A685BD]" data-testid={`lead-delete-${l.id}`}><Trash2 size={14} /></button>
          </td>
        );
      default:
        return <td />;
    }
  };

  return (
    <>
      <thead className="bg-[#FAF6EE] text-left text-[#5D2C8F]">
        <tr>
          {visible.map((id) => (
            <th key={id} className={`px-3 py-3 ${widthFor(id)}`} data-testid={`leads-col-${id}`}>{th(id)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id} className={`border-t border-[#E5E0D8] hover:bg-[#FAF6EE]/70 group relative ${selected.has(l.id) ? "bg-[#FAF6EE]" : ""}`} data-testid={`lead-row-${l.id}`}>
            {visible.map((id) => <React.Fragment key={id}>{td(id, l)}</React.Fragment>)}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={visible.length} className="px-4 py-10 text-center text-[#6E6074]">No leads yet. Click <em>Add lead</em> or <em>Import from spreadsheet</em>.</td></tr>}
      </tbody>
    </>
  );
}

function ConvertLeadModal({ lead, settings, onClose, onSubmit }) {
  const [term, setTerm] = useState(settings?.current_term || "Summer 2026");
  const [sessionKey, setSessionKey] = useState(
    SESSION_OPTS.some((o) => o.v === lead.session_preference) ? lead.session_preference : "tue_am"
  );
  const [studentType, setStudentType] = useState("new");
  const [includeStarter, setIncludeStarter] = useState(true);
  const [createInvoice, setCreateInvoice] = useState(true);
  const termOptions = useMemo(() => {
    // Show current + all future terms so a lead can be enrolled into the
    // upcoming term ("Autumn 2026") as well as the term that's running
    // right now. Past terms are omitted — you can't enrol someone into a
    // term that's already ended. Sorted ascending by start_date so the
    // current term appears first and upcoming ones follow naturally.
    const all = (settings?.terms || [])
      .filter((t) => t && t.term && !t.is_past)
      .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
    const names = all.map((t) => t.term);
    if (settings?.current_term && !names.includes(settings.current_term)) {
      names.unshift(settings.current_term);
    }
    return names.length ? names : [term];
  }, [settings, term]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="admin-surface w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="convert-lead-modal">
        <h3 className="font-display text-2xl text-[#5D2C8F] mb-1 flex items-center gap-2"><GraduationCap size={18} /> Enrol {lead.name?.split(" ")[0]}</h3>
        <p className="text-sm text-[#6E6074] mb-4">This adds <strong>{lead.name}</strong> to the term roster and (optionally) creates a draft invoice ready to send.</p>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-[#5D2C8F]">Term</label>
            <select value={term} onChange={(e) => setTerm(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="convert-term">
              {termOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-[#5D2C8F]">Session</label>
            <select value={sessionKey} onChange={(e) => setSessionKey(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="convert-session">
              {SESSION_OPTS.filter((o) => o.v).map((o) => <option key={o.v} value={o.v}>{o.lbl}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-[#5D2C8F]">Student type</label>
            <select value={studentType} onChange={(e) => setStudentType(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="convert-type">
              <option value="new">New student</option>
              <option value="returner">Returner (£{settings?.returner_discount || 30} discount)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#5D2C8F] pt-2">
            <input type="checkbox" checked={createInvoice} onChange={(e) => setCreateInvoice(e.target.checked)} data-testid="convert-create-invoice" />
            Auto-create draft invoice
          </label>
          {createInvoice && (
            <label className="flex items-center gap-2 text-sm text-[#5D2C8F] pl-6">
              <input type="checkbox" checked={includeStarter} onChange={(e) => setIncludeStarter(e.target.checked)} data-testid="convert-starter-pack" />
              Include Starter pack add-on
            </label>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]" data-testid="convert-cancel">Cancel</button>
          <button onClick={() => onSubmit({ term, session_key: sessionKey, student_type: studentType, create_invoice: createInvoice, include_starter_pack: includeStarter })} className="sac-btn-primary" data-testid="convert-submit">Enrol & create invoice</button>
        </div>
      </div>
    </div>
  );
}

function HoverNotes({ notes }) {
  return (
    <div className="mt-1">
      <div className="text-xs text-[#6E6074] line-clamp-1 max-w-md">{notes}</div>
      <div className="absolute left-4 right-4 mt-1 z-20 hidden group-hover:block">
        <div className="bg-[#2b1840] text-[#F4EDDD] text-xs rounded-lg p-3 shadow-xl max-w-xl whitespace-pre-wrap leading-relaxed">
          {notes}
        </div>
      </div>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onEdit, onStatus, onDelete, onSendAck, STATUSES, STATUS_LABEL, STATUS_COLOR }) {
  return (
    <div className="fixed inset-0 z-40 flex" data-testid={`lead-drawer-${lead.id}`}>
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-xl bg-[#FAF6EE] border-l border-[#E5E0D8] h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right">
        <div className="sticky top-0 bg-[#FAF6EE] border-b border-[#E5E0D8] px-6 py-4 flex items-start justify-between">
          <div>
            <div className="font-hand text-[#A685BD] text-sm">contact</div>
            <h2 className="font-display text-3xl text-[#5D2C8F] leading-tight">{lead.name}</h2>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded-full border border-[#E5E0D8]" style={{ color: STATUS_COLOR[lead.status] }}>{lead.status}</span>
              {lead.source && <span className="text-xs text-[#6E6074]">via {lead.source}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white" data-testid="lead-drawer-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <DetailRow icon={Mail} label="Email" value={lead.email ? <a className="hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a> : '—'} />
            <DetailRow icon={Phone} label="Phone" value={lead.phone ? <a className="hover:underline" href={`tel:${(lead.phone||'').replace(/\s+/g,'')}`}>{lead.phone}</a> : '—'} />
            <DetailRow icon={Calendar} label="Preferred session" value={lead.session_preference || '—'} />
            <DetailRow icon={Calendar} label="Enquiry date" value={lead.enquiry_date || new Date(lead.created_at).toLocaleDateString('en-GB')} />
          </div>

          {lead.enquiry_ack_sent_at && (
            <div className="bg-[#8EC9C7]/15 border border-[#8EC9C7]/30 text-[#5D2C8F] text-xs rounded-lg px-3 py-2 inline-flex items-center gap-2" data-testid="lead-drawer-ack-status">
              <MailCheck size={13} /> Thank-you email sent {new Date(lead.enquiry_ack_sent_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}

          <div className="admin-surface p-4">
            <div className="flex items-center gap-2 text-[#5D2C8F] mb-2"><NotebookPen size={15} /> <span className="text-sm font-medium">Notes & activity log</span></div>
            <div className="text-sm text-[#3A2A47] whitespace-pre-wrap leading-relaxed">
              {lead.notes || <span className="text-[#6E6074]">No notes yet.</span>}
            </div>
          </div>

          {(lead.starter_pack || lead.first_payment) && (
            <div className="admin-surface p-4">
              <div className="text-sm font-medium text-[#5D2C8F] mb-2">Enrolment details</div>
              {lead.starter_pack && <div className="text-sm text-[#3A2A47]"><strong>Starter pack / allergies:</strong> {lead.starter_pack}</div>}
              {lead.first_payment && <div className="text-sm text-[#3A2A47]"><strong>First payment:</strong> {lead.first_payment}</div>}
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="text-sm text-[#5D2C8F]">Update status</label>
            <select value={lead.status} onChange={(e) => onStatus(e.target.value)} className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-1.5 text-sm" data-testid="lead-drawer-status">
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
            </select>
          </div>

          <div className="flex gap-3 pt-2 border-t border-[#E5E0D8] flex-wrap">
            <button
              onClick={onSendAck}
              disabled={!lead.email}
              className="sac-btn-primary !py-2 !px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="lead-drawer-send-ack"
            >
              <Send size={14} /> {lead.enquiry_ack_sent_at ? "Re-send thank-you" : "Send thank-you email"}
            </button>
            <button onClick={onEdit} className="sac-btn-secondary !py-2 !px-4 text-sm" data-testid="lead-drawer-edit"><Edit3 size={14} /> Edit</button>
            <button onClick={onDelete} className="text-sm text-[#A685BD] hover:underline ml-auto" data-testid="lead-drawer-delete">Delete lead</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="admin-surface p-3">
      <div className="flex items-center gap-2 text-xs text-[#6E6074] uppercase tracking-widest"><Icon size={12} /> {label}</div>
      <div className="text-sm text-[#3A2A47] mt-1 break-words">{value}</div>
    </div>
  );
}

function LeadForm({ lead, onClose, onSave, STATUSES, STATUS_LABEL }) {
  const [f, setF] = useState({
    name: lead?.name || "", email: lead?.email || "", phone: lead?.phone || "",
    source: lead?.source || "", session_preference: lead?.session_preference || "",
    notes: lead?.notes || "", status: lead?.status || "new",
  });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="admin-surface w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-form-modal">
        <h3 className="font-display text-2xl text-[#5D2C8F] mb-4">{lead?.id ? "Edit lead" : "Add lead"}</h3>
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Name*" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="leadform-name" />
          <Inp label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="leadform-email" />
          <Inp label="Phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="leadform-phone" />
          <Inp label="Source" value={f.source} onChange={(v) => setF({ ...f, source: v })} testid="leadform-source" />
          <div className="col-span-2">
            <label className="text-sm text-[#5D2C8F]">Session preference</label>
            <select value={SESSION_OPTS.some(o => o.v === f.session_preference) ? f.session_preference : ""}
              onChange={(e) => setF({ ...f, session_preference: e.target.value })}
              className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="leadform-session">
              {SESSION_OPTS.map(o => <option key={o.v} value={o.v}>{o.lbl}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-sm text-[#5D2C8F]">Notes</label>
            <textarea rows={6} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="leadform-notes" />
          </div>
          <div className="col-span-2">
            <label className="text-sm text-[#5D2C8F]">Status</label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
              className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="leadform-status">
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]" data-testid="leadform-cancel-btn">Cancel</button>
          <button onClick={() => onSave(f)} className="sac-btn-primary" data-testid="leadform-save-btn">Save</button>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-sm text-[#5D2C8F]">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid={testid} />
    </div>
  );
}


/**
 * Send-enquiry-acknowledgement modal.
 * Loads the stored template via GET /api/admin/enquiry-ack-template, lets the
 * admin tweak the subject + body for THIS send only (no save back to template
 * unless they hit "Save as default"). Live-renders a server-side preview so
 * merge-tags like {{name}} show actual values.
 */
function SendAckModal({ lead, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/enquiry-ack-template");
        setSubject(data.subject || "");
        setBodyHtml(data.body_html || "");
      } catch { toast.error("Couldn't load template"); onClose(); }
      finally { setLoading(false); }
    })();
  }, [onClose]);

  // Live preview merge — mirror of the server-side substitution so admins see
  // the email exactly as the recipient will. Kept in-sync with the backend
  // string-replace block in `enquiry_ack_html`.
  const sessionLabelOpts = {
    tue_am: "Tuesday AM (10am–1pm)",
    tue_pm: "Tuesday PM (2pm–5pm)",
    fri_eve: "Friday Evening (6pm–8:30pm)",
  };
  const previewHtml = (bodyHtml || "")
    .replaceAll("{{name}}", (lead?.name || "there").split(" ")[0] || "there")
    .replaceAll("{{session_preference}}", sessionLabelOpts[lead?.session_preference] || "your preferred session")
    .replaceAll("{{enquiry_date}}", lead?.enquiry_date || "")
    .replaceAll("{{studio_name}}", "The Seasons Art Class East Croydon")
    .replaceAll("{{studio_url}}", "https://www.artclassesineastcroydon.co.uk")
    .replaceAll("{{signature_name}}", "Jadesola Ayoola");

  const send = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/send-enquiry-ack`, {
        subject, body_html: bodyHtml,
      });
      onSent(data.lead);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const saveAsDefault = async () => {
    setSavingDefault(true);
    try {
      await api.put("/admin/enquiry-ack-template", { subject, body_html: bodyHtml });
      toast.success("Saved as default — future enquiry replies start from this version");
    } catch { toast.error("Couldn't save"); }
    finally { setSavingDefault(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="admin-surface w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="send-ack-modal">
        <div className="px-6 py-4 border-b border-[#E5E0D8] flex items-start justify-between gap-3">
          <div>
            <div className="font-hand text-[#A685BD] text-sm">Send thank-you email</div>
            <h3 className="font-display text-2xl text-[#5D2C8F]">To {lead.name} <span className="text-base text-[#6E6074]">· {lead.email}</span></h3>
            <p className="text-xs text-[#6E6074] mt-1">Edit the wording below — use <code className="bg-white px-1 rounded">{`{{name}}`}</code>, <code className="bg-white px-1 rounded">{`{{session_preference}}`}</code>, <code className="bg-white px-1 rounded">{`{{signature_name}}`}</code> as merge tags.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white" data-testid="send-ack-close"><X size={18} /></button>
        </div>

        <div className="grid lg:grid-cols-2 gap-0 flex-1 overflow-hidden">
          {/* Editor */}
          <div className="p-5 space-y-3 overflow-y-auto border-r border-[#E5E0D8]">
            <div>
              <label className="text-sm text-[#5D2C8F]">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={loading} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="send-ack-subject" />
            </div>
            <div>
              <label className="text-sm text-[#5D2C8F]">Message body (HTML supported)</label>
              <textarea
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                disabled={loading}
                rows={18}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-xs font-mono leading-relaxed"
                data-testid="send-ack-body"
              />
            </div>
            <div>
              <button onClick={saveAsDefault} disabled={savingDefault || loading} className="text-xs text-[#5D2C8F] hover:underline disabled:opacity-50" data-testid="send-ack-save-default">
                {savingDefault ? "Saving…" : "Save these tweaks as the new default template"}
              </button>
            </div>
          </div>

          {/* Live preview */}
          <div className="p-5 bg-[#FAF6EE]/50 overflow-y-auto" data-testid="send-ack-preview">
            <div className="text-[10px] uppercase tracking-widest text-[#A685BD] mb-2">Live preview · {lead.email}</div>
            <div className="text-sm font-medium text-[#5D2C8F] mb-3 bg-white rounded p-2 border border-[#E5E0D8]">{subject || "(no subject)"}</div>
            <div className="prose prose-sm max-w-none bg-white rounded-lg p-4 border border-[#E5E0D8] text-[#3A2A47]" dangerouslySetInnerHTML={safeHtml(previewHtml)} />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E5E0D8] flex items-center justify-between">
          <div className="text-xs text-[#6E6074]">
            {lead.enquiry_ack_sent_at ? <>Last sent {new Date(lead.enquiry_ack_sent_at).toLocaleString('en-GB')}</> : "Not yet sent"}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-sm text-[#6E6074]" data-testid="send-ack-cancel">Cancel</button>
            <button onClick={send} disabled={sending || loading || !lead.email} className="sac-btn-primary disabled:opacity-50" data-testid="send-ack-submit">
              <Send size={14} /> {sending ? "Sending…" : "Send email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
